import { useId, useState, type ReactNode } from "react";
import { ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { InteractiveElementOverlay } from "@/components/interactive/InteractiveElementOverlay";
import InteractiveElementOverlayFooter from "@/components/interactive/InteractiveElementOverlayFooter";
import InteractiveElementOverlayHeader from "@/components/interactive/InteractiveElementOverlayHeader";
import InteractiveElementCard, {
  type TaskStatusBadge,
} from "@/components/interactive/InteractiveElementCard";
import InteractiveElementInlineCard, {
  INTERACTIVE_ELEMENT_CARD_CLASS,
} from "@/components/interactive/InteractiveElementInlineCard";
import { InteractiveSplitOverlayContext } from "@/components/interactive/interactiveSplitOverlayContext";
import {
  CODE_QUIZ_MODAL_CLASS_NAME,
  ModalSplitCloseButton,
} from "@/components/layout/ModalSplitLayout";
import {
  useInteractiveElementState,
  type UseInteractiveElementStateApi,
} from "@/hooks/useInteractiveElementState";
import {
  InteractiveElementStatusKind,
  type InteractiveElementScore,
  type InteractiveElementStatus,
} from "@/lib/interactiveElementState";
import type { InteractiveIllustrationKind } from "@/lib/interactiveIllustration";

interface CtaConfig {
  label: string;
  onStart?: () => void;
}

interface Props {
  contentElementId: string;
  enrollmentId: string | null;
  title: string;
  illustrationKind: InteractiveIllustrationKind;
  intro?: ReactNode;
  notStartedCta?: CtaConfig;
  inProgressCta?: CtaConfig;
  completedView: (
    status: Extract<
      InteractiveElementStatus,
      { kind: InteractiveElementStatusKind.Completed }
    >,
    api: UseInteractiveElementStateApi,
    ui: {
      /** Opens the overlay — e.g. to review a completed element read-only. */
      open: () => void;
      isEditing: boolean;
    }
  ) => ReactNode;
  editingView: (
    api: UseInteractiveElementStateApi & {
      close: () => void;
    }
  ) => ReactNode;
  supportsInProgress?: boolean;
  /**
   * Fires right before the overlay closes. The consumer uses this to persist
   * in-progress state — e.g. to mark the element as "inProgress" instead of
   * letting it revert to "notStarted".
   */
  onBeforeClose?: () => void;
  statusOverride?: InteractiveElementStatus;
  /**
   * Overlay size. "wide" opens a full-viewport split modal without a title
   * bar — use it for elements that need room to work, e.g. code editors.
   * Defaults to the standard centered card.
   */
  size?: "default" | "wide";
  /**
   * "modal" opens editing in an overlay (default). "inline" renders the active
   * task directly inside the blue card with no start CTA.
   */
  layout?: "modal" | "inline";
  /**
   * Wording of the CTA that leaves a completed overlay. Defaults to "Fertig";
   * elements the learner hands in say so ("Abschließen").
   */
  completedCtaLabel?: string;
  /**
   * When false, a finished card skips the Abgeschlossen pill. Angefangen still
   * shows while the quiz can be worked on. Code quizzes use this so the start
   * card stays unlabeled once every task is over.
   */
  showCompletedBadge?: boolean;
  /**
   * Shows the overlay close CTA while the card stays a start card. Code
   * quizzes use this once every task is finished, so the learner can leave
   * the result without the card switching to the inline recap.
   */
  showCloseCta?: boolean;
}

const DEFAULT_START_LABEL = "Starten";
const DEFAULT_CONTINUE_LABEL = "Weitermachen";
const FINISHED_QUIZ_CTA_LABEL = "Ergebnis anzeigen";

const OVERLAY_SIZE_CLASS: Record<"default" | "wide", string | undefined> = {
  default: undefined,
  wide: CODE_QUIZ_MODAL_CLASS_NAME,
};

/**
 * Shared card + optional overlay for interactive storyboard elements
 * (quizzes, worksheets, reflections).
 */
export const InteractiveElementShell = ({
  contentElementId,
  enrollmentId,
  title,
  illustrationKind,
  intro,
  notStartedCta,
  inProgressCta,
  completedView,
  editingView,
  supportsInProgress,
  onBeforeClose,
  statusOverride,
  size = "default",
  layout = "modal",
  completedCtaLabel = "Fertig",
  showCompletedBadge = true,
  showCloseCta = false,
}: Props) => {
  const api = useInteractiveElementState(contentElementId, enrollmentId);
  const status = statusOverride ?? api.status;
  const isCompleted = status.kind === InteractiveElementStatusKind.Completed;
  const overlayCloseCtaVisible = isCompleted || showCloseCta;
  const [isEditing, setEditing] = useState(false);
  const titleId = useId();
  const isInline = layout === "inline";
  const isSplitOverlay = size === "wide";

  const open = () => setEditing(true);
  const close = () => {
    if (isInline) {
      // Mid-task: no overlay to leave. After completion: dismiss the last-step
      // feedback / summary and show the completed card chrome.
      setEditing(false);
      return;
    }
    onBeforeClose?.();
    setEditing(false);
  };

  const completeAndStayOpen = (
    score?: InteractiveElementScore,
    review?: unknown
  ) => {
    api.complete(score, review);
    // Match modal: keep the task UI mounted so the learner still sees final
    // feedback before dismissing to the completed summary.
    if (isInline) setEditing(true);
  };

  const startButton = (cfg: CtaConfig) => (
    <Button
      size="2xl"
      onClick={() => {
        cfg.onStart?.();
        open();
      }}
    >
      {cfg.label}
      <ArrowRight aria-hidden="true" />
    </Button>
  );

  const renderCompleted = (
    s: Extract<
      InteractiveElementStatus,
      { kind: InteractiveElementStatusKind.Completed }
    >
  ) => (
    <div className="flex flex-col items-start gap-4">
      {completedView(s, api, { open, isEditing })}
    </div>
  );

  const isInProgress = status.kind === InteractiveElementStatusKind.InProgress;
  const resumeInProgress = isInProgress && Boolean(supportsInProgress);
  // Stored progress can already be completed while the card stays reopenable,
  // so the learner reviews the result instead of resuming an open attempt.
  const learnerAlreadyFinished =
    api.status.kind === InteractiveElementStatusKind.Completed;
  const resumeCta = inProgressCta ?? { label: DEFAULT_CONTINUE_LABEL };
  const startCta = resumeInProgress
    ? {
        ...resumeCta,
        label: learnerAlreadyFinished
          ? FINISHED_QUIZ_CTA_LABEL
          : resumeCta.label,
      }
    : (notStartedCta ?? { label: DEFAULT_START_LABEL });

  const showStartBody =
    status.kind === InteractiveElementStatusKind.NotStarted || isInProgress;

  const renderModalCardAction = () => {
    if (status.kind === InteractiveElementStatusKind.Completed) {
      return renderCompleted(status);
    }
    return startButton(startCta);
  };

  const introOnTheStartCard = () => {
    if (!showStartBody) return undefined;
    return intro;
  };

  // Angefangen while a modal can still be worked on. Finished code quizzes
  // stay a start card and skip the pill. Other finished modals read
  // Abgeschlossen in the same info colors.
  const cardStatusBadge = (): TaskStatusBadge | null => {
    const openQuizShowsAngefangen =
      !learnerAlreadyFinished && !isInline && resumeInProgress;
    if (openQuizShowsAngefangen) return "in_progress";

    const finishedCardHidesBadge =
      learnerAlreadyFinished && !showCompletedBadge;
    const cardShowsNoBadge = !learnerAlreadyFinished || finishedCardHidesBadge;
    if (cardShowsNoBadge) return null;

    if (isInline) return "completed";
    return "finished";
  };

  // Recaps stack under the title row like inline quizzes; start cards keep
  // the CTA in the column beside the illustration.
  const cardBodyPlacement = () => {
    if (isCompleted) return "below" as const;
    return "beside" as const;
  };

  const editingApi = {
    ...api,
    status,
    complete: completeAndStayOpen,
    close,
  };

  const renderFertigSoLearnerCanLeaveFeedback = () => {
    const learnerCanDismissLastStepFeedback = isCompleted && isEditing;
    if (!learnerCanDismissLastStepFeedback) return null;

    return (
      <div className="mt-4 flex justify-center">
        <Button size="xl" onClick={close}>
          {completedCtaLabel}
        </Button>
      </div>
    );
  };

  const renderInlineBody = () => {
    const learnerShouldSeeCompletedCard = isCompleted && !isEditing;
    if (!learnerShouldSeeCompletedCard) {
      return (
        <>
          {editingView(editingApi)}
          {renderFertigSoLearnerCanLeaveFeedback()}
        </>
      );
    }

    return renderCompleted(status);
  };

  const renderOverlayHeader = () => {
    if (!isSplitOverlay) {
      return (
        <InteractiveElementOverlayHeader
          title={title}
          showCloseButton={!isCompleted}
          onClose={close}
        />
      );
    }

    // Lernkarten-style: no title bar, just the corner close control. The
    // completed review closes through the footer's Fertig instead.
    return isCompleted ? null : (
      <ModalSplitCloseButton label="Fertig" onClose={close} />
    );
  };

  if (isInline) {
    return (
      <InteractiveElementInlineCard
        title={title}
        titleId={titleId}
        illustrationKind={illustrationKind}
      >
        {renderInlineBody()}
      </InteractiveElementInlineCard>
    );
  }

  return (
    <section aria-labelledby={titleId} className="interactive-element">
      <div
        data-slot="interactive-element-card"
        className={INTERACTIVE_ELEMENT_CARD_CLASS}
      >
        <InteractiveElementCard
          illustrationKind={illustrationKind}
          title={title}
          titleId={titleId}
          intro={introOnTheStartCard()}
          action={renderModalCardAction()}
          statusBadge={cardStatusBadge()}
          bodyPlacement={cardBodyPlacement()}
        />
      </div>

      <InteractiveElementOverlay
        open={isEditing}
        onClose={close}
        labelledBy={titleId}
        modalClassName={OVERLAY_SIZE_CLASS[size]}
        fullBleed={isSplitOverlay}
        // Split content paints its panes edge to edge and scrolls per pane on
        // desktop, so the body drops its padding and outer scroll there.
        bodyClassName={
          isSplitOverlay ? "min-h-0 lg:overflow-hidden" : undefined
        }
        header={renderOverlayHeader()}
        footer={
          <InteractiveElementOverlayFooter
            visible={overlayCloseCtaVisible}
            onClose={close}
            label={completedCtaLabel}
            bordered={!showCloseCta}
          />
        }
      >
        <InteractiveSplitOverlayContext.Provider
          value={isSplitOverlay ? { title } : null}
        >
          {editingView(editingApi)}
        </InteractiveSplitOverlayContext.Provider>
      </InteractiveElementOverlay>
    </section>
  );
};
